"use client";

import { useState, useRef, useEffect, useTransition } from "react";
import { useRouter } from "next/navigation";
import { ChevronsUpDown, Check, Building2 } from "lucide-react";
import { setActiveCustomerAction } from "@/app/actions";

export default function CustomerSwitcher({
  customers,
  activeId,
}: {
  customers: Array<{ id: string; name: string }>;
  activeId?: string;
}) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [pending, start] = useTransition();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const active = customers.find((c) => c.id === activeId) ?? customers[0];

  function choose(id: string) {
    setOpen(false);
    if (id === active?.id) return;
    start(async () => {
      await setActiveCustomerAction(id);
      router.refresh();
    });
  }

  if (customers.length === 0) {
    return (
      <div
        className="flex items-center gap-2 rounded-[6px] px-3 py-2 text-[13px] font-light"
        style={{ color: "var(--c-on-dark-soft)", boxShadow: "inset 0 0 0 1px var(--c-on-dark-hairline)" }}
      >
        <Building2 size={14} /> no customer yet
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        className="flex w-full items-center gap-2 rounded-[6px] px-3 py-2 text-left transition-colors duration-200"
        style={{
          background: open ? "var(--c-green)" : "transparent",
          color: open ? "var(--c-charcoal)" : "var(--c-white)",
          boxShadow: open ? "none" : "inset 0 0 0 1px var(--c-on-dark-hairline)",
          opacity: pending ? 0.6 : 1,
        }}
        onClick={() => setOpen((o) => !o)}
      >
        <Building2 size={14} className="shrink-0" />
        <span className="min-w-0 flex-1 truncate text-[13px] font-medium">{active?.name}</span>
        <ChevronsUpDown size={14} className="shrink-0" style={{ opacity: 0.6 }} />
      </button>

      {open && (
        <div
          className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-[6px] py-1"
          style={{ background: "var(--c-white)" }}
        >
          <div className="eyebrow px-3 pb-1 pt-2" style={{ color: "var(--c-ink-soft)" }}>
            viewing as
          </div>
          {customers.map((c) => (
            <button
              key={c.id}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] transition-colors duration-150 hover:bg-[var(--c-cream)]"
              style={{ color: "var(--c-charcoal)", fontWeight: c.id === active?.id ? 500 : 300 }}
              onClick={() => choose(c.id)}
            >
              <span className="min-w-0 flex-1 truncate">{c.name}</span>
              {c.id === active?.id && <Check size={14} style={{ color: "var(--c-charcoal)" }} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
